import { useState } from 'react';

import { AudioBanner } from '../components/AudioBanner';
import { ContentBand } from '../components/ContentBand';
import { audioTransitions, getAudioContext } from '../core/speech/audioUnlock';
import { playAttentionTone } from '../core/speech/attentionTone';
import { useAudioStatus } from '../core/speech/audioStatusStore';
import { speechService } from '../core/speech/SpeechService';
import { audioCacheSize } from '../core/speech/audioCache';
import { getSettings } from '../core/profile/ProfileStore';

/**
 * Device test harness at `?dev=audio`, until Betreuer-Modus (1.13) takes over.
 *
 * This exists because the audio questions cannot wait for the surfaces. Two of them are
 * unanswerable anywhere but on the real iPad:
 *
 *  - **is speech audible with Control Center mute on** — Phase 0 never ran that probe, and every
 *    utterance now routes through Web Audio, so if mute suppresses it the whole speech function is
 *    unreliable rather than merely inconvenient;
 *  - **what actually re-locks the AudioContext** — Phase 0 never saw it leave `running`, so the
 *    watchdog is currently written against an untested threat. The transition log below is how we
 *    find out from the field instead of guessing.
 *
 * Building the keyboard first and testing audio in two weeks would mean discovering both on a
 * device that is by then supposed to be finished.
 */

const buttonStyle: React.CSSProperties = {
  minHeight: 'var(--min-target)',
  padding: '8px 16px',
  fontSize: 'var(--font-button)',
  background: 'var(--key-bg)',
  color: 'var(--key-fg)',
  border: '3px solid var(--key-border)',
  borderRadius: 'var(--radius)',
};

export function DevAudio(): React.JSX.Element {
  const status = useAudioStatus();
  const [log, setLog] = useState<string[]>([]);
  const [cacheCount, setCacheCount] = useState<number | null>(null);

  const note = (line: string): void => setLog((prev) => [...prev.slice(-14), line]);

  const speak = async (text: string, isPhrase: boolean): Promise<void> => {
    const startedAt = performance.now();
    const result = await speechService.speak({ text, source: 'keyboard', isPhrase });
    const elapsed = Math.round(performance.now() - startedAt);
    note(
      result
        ? `${result.providerId}${result.cached ? ' (cache)' : ''} ${elapsed}ms${result.silent ? ' SILENT!' : ''}`
        : `abgebrochen ${elapsed}ms`,
    );
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 16 }}>
      <AudioBanner />
      <ContentBand>
        <h1 style={{ fontSize: 28, marginBottom: 12 }}>Audio-Test</h1>

        <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', marginBottom: 16 }}>
          <dt>AudioContext</dt>
          <dd data-testid="audio-state">{status.state}</dd>
          <dt>entsperrt</dt>
          <dd data-testid="audio-unlocked">{status.unlocked ? 'ja' : 'nein'}</dd>
          <dt>audioSession</dt>
          <dd data-testid="audio-session">{status.sessionConfigured ?? '–'}</dd>
          <dt>Stimme</dt>
          <dd>{getSettings().voiceId}</dd>
          <dt>Audio im Cache</dt>
          <dd>{cacheCount ?? '–'}</dd>
        </dl>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <button type="button" style={buttonStyle} onClick={() => void status.unlockFromGesture()}>
            Ton aktivieren
          </button>
          <button
            type="button"
            style={buttonStyle}
            data-testid="dev-speak-short"
            onClick={() => void speak('Ja', true)}
          >
            «Ja» sprechen
          </button>
          <button
            type="button"
            style={buttonStyle}
            data-testid="dev-speak-sentence"
            onClick={() => void speak('Ich hätte gern ein Glas Wasser, bitte nicht zu kalt.', true)}
          >
            Satz sprechen
          </button>
          <button
            type="button"
            style={buttonStyle}
            onClick={() =>
              void speak(
                'Heute Morgen war ich schon wach, bevor die Sonne aufging, und ich habe lange ' +
                  'überlegt, ob ich dir das alles wirklich erzählen soll oder es lieber für mich ' +
                  'behalten möchte.',
                false,
              )
            }
          >
            Langer Satz
          </button>
          <button type="button" style={buttonStyle} onClick={() => void playAttentionTone()}>
            Signalton
          </button>
          {/*
            Stand in for iOS backgrounding the app (C71). Screen lock, Siri and phone calls do not exist in
            the flow harness; a suspended context does, and that turned out to be the part worth being able
            to reach — the freeze a tester reported was a suspended context nothing noticed.
          */}
          <button
            type="button"
            style={buttonStyle}
            data-testid="dev-suspend"
            onClick={() => {
              void getAudioContext()
                ?.suspend()
                .then(() => note('KONTEXT ANGEHALTEN'));
            }}
          >
            Kontext anhalten
          </button>
          <button
            type="button"
            style={{ ...buttonStyle, background: 'var(--danger)', color: 'var(--danger-fg)' }}
            data-testid="dev-stop"
            onClick={() => {
              speechService.stop();
              note('STOPP');
            }}
          >
            Stopp
          </button>
          <button
            type="button"
            style={buttonStyle}
            onClick={() => void audioCacheSize().then(setCacheCount)}
          >
            Cache zählen
          </button>
        </div>

        <h2 style={{ fontSize: 20, marginBottom: 4 }}>Verlauf</h2>
        <ol data-testid="dev-log" style={{ fontSize: 16, marginBottom: 16, paddingLeft: 20 }}>
          {log.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ol>

        <h2 style={{ fontSize: 20, marginBottom: 4 }}>AudioContext-Wechsel</h2>
        {/* The reason this screen exists: lock the iPad, take a call, summon Siri, then read this. */}
        <ol style={{ fontSize: 16, paddingLeft: 20 }} data-testid="audio-trail">
          {audioTransitions().map((transition, index) => (
            <li key={index}>
              {transition.from} → {transition.to} ({transition.visibility}, {transition.atMs} ms)
            </li>
          ))}
        </ol>
      </ContentBand>
    </div>
  );
}
